/**
 * Aggregation Demo Page
 *
 * Shows a working AggregationView with mock participant data for development/preview.
 * Remove or gate with a feature flag before production.
 */
import { useEffect, useState } from 'react';
import { AggregationView } from '../../../components/prioritization/tools';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';

type DemoTab = 'classification' | 'ranking';

export default function AggregationDemoPage() {
  const createToolSession = useToolSessionStore(s => s.createToolSession);
  const submitParticipantResponse = useToolSessionStore(s => s.submitParticipantResponse);
  const sessions = useToolSessionStore(s => s.toolSessions);

  const [activeTab, setActiveTab] = useState<DemoTab>('classification');
  const [eisenhowerSessionId, setEisenhowerSessionId] = useState<string | null>(null);
  const [riceSessionId, setRiceSessionId] = useState<string | null>(null);

  useEffect(() => {
    // Only seed once — if there are already sessions, skip
    if (sessions.length > 0) {
      // Try to find existing sessions by type
      const existing = useToolSessionStore.getState().toolSessions;
      const eis = existing.find(s => s.toolType === 'eisenhower-matrix');
      const rice = existing.find(s => s.toolType === 'rice-scoring');
      if (eis) setEisenhowerSessionId(eis.id);
      if (rice) setRiceSessionId(rice.id);
      return;
    }

    // ── Eisenhower (classification) demo ────────────────────────────────────
    createToolSession({
      toolType: 'eisenhower-matrix',
      toolName: 'Eisenhower Matrix',
      outputType: 'classification',
      mode: 'sync',
      participantIds: ['p1', 'p2', 'p3', 'p4'],
      participantNames: { p1: 'Alice Chen', p2: 'Bob Martinez', p3: 'Carol Singh', p4: 'David Park' },
      facilitatorId: 'facilitator-1',
      facilitatorName: 'Sam (Facilitator)',
      items: [
        { id: 'item-1', title: 'Fix critical login bug', description: 'Users cannot log in on Safari' },
        { id: 'item-2', title: 'Redesign onboarding flow', description: 'Improve activation rate' },
        { id: 'item-3', title: 'Update API documentation', description: 'Outdated since v2.0' },
        { id: 'item-4', title: 'Add SSO support', description: 'Enterprise customer requirement' },
        { id: 'item-5', title: 'Performance audit', description: 'P95 latency has degraded' },
        { id: 'item-6', title: 'Holiday party planning', description: 'Annual team event' },
      ],
    });

    const eiSession = useToolSessionStore.getState().toolSessions.at(-1);
    if (!eiSession) return;
    const eiSid = eiSession.id;
    setEisenhowerSessionId(eiSid);

    submitParticipantResponse({
      toolSessionId: eiSid,
      participantId: 'p1',
      participantName: 'Alice Chen',
      results: [
        { id: 'item-1', name: 'Fix critical login bug', category: 'ui', score: 4 },
        { id: 'item-2', name: 'Redesign onboarding flow', category: 'in', score: 3 },
        { id: 'item-3', name: 'Update API documentation', category: 'un', score: 2 },
        { id: 'item-4', name: 'Add SSO support', category: 'in', score: 3 },
        { id: 'item-5', name: 'Performance audit', category: 'ui', score: 4 },
        { id: 'item-6', name: 'Holiday party planning', category: 'nn', score: 1 },
      ],
    });

    submitParticipantResponse({
      toolSessionId: eiSid,
      participantId: 'p2',
      participantName: 'Bob Martinez',
      results: [
        { id: 'item-1', name: 'Fix critical login bug', category: 'ui', score: 4 },
        { id: 'item-2', name: 'Redesign onboarding flow', category: 'ui', score: 4 },
        { id: 'item-3', name: 'Update API documentation', category: 'nn', score: 1 },
        { id: 'item-4', name: 'Add SSO support', category: 'in', score: 3 },
        { id: 'item-5', name: 'Performance audit', category: 'ui', score: 4 },
        { id: 'item-6', name: 'Holiday party planning', category: 'nn', score: 1 },
      ],
    });

    submitParticipantResponse({
      toolSessionId: eiSid,
      participantId: 'p3',
      participantName: 'Carol Singh',
      results: [
        { id: 'item-1', name: 'Fix critical login bug', category: 'ui', score: 4 },
        { id: 'item-2', name: 'Redesign onboarding flow', category: 'in', score: 3 },
        { id: 'item-3', name: 'Update API documentation', category: 'un', score: 2 },
        { id: 'item-4', name: 'Add SSO support', category: 'ui', score: 4 },
        { id: 'item-5', name: 'Performance audit', category: 'in', score: 3 },
        { id: 'item-6', name: 'Holiday party planning', category: 'un', score: 2 },
      ],
    });

    submitParticipantResponse({
      toolSessionId: eiSid,
      participantId: 'p4',
      participantName: 'David Park',
      results: [
        { id: 'item-1', name: 'Fix critical login bug', category: 'ui', score: 4 },
        { id: 'item-2', name: 'Redesign onboarding flow', category: 'in', score: 3 },
        { id: 'item-3', name: 'Update API documentation', category: 'un', score: 2 },
        { id: 'item-4', name: 'Add SSO support', category: 'in', score: 3 },
        { id: 'item-5', name: 'Performance audit', category: 'ui', score: 4 },
        { id: 'item-6', name: 'Holiday party planning', category: 'nn', score: 1 },
      ],
    });

    // ── RICE Scoring (scoring/ranking) demo ──────────────────────────────────
    createToolSession({
      toolType: 'rice-scoring',
      toolName: 'RICE Scoring',
      outputType: 'scoring',
      mode: 'sync',
      participantIds: ['q1', 'q2', 'q3'],
      participantNames: { q1: 'Priya Nair', q2: 'Tom Wells', q3: 'Yuki Tanaka' },
      facilitatorId: 'facilitator-1',
      facilitatorName: 'Sam (Facilitator)',
      items: [
        { id: 'feat-1', title: 'Mobile push notifications', description: 'Re-engagement for churned users' },
        { id: 'feat-2', title: 'Bulk import via CSV', description: 'Power-user data entry' },
        { id: 'feat-3', title: 'Role-based permissions', description: 'Enterprise access control' },
        { id: 'feat-4', title: 'Dark mode', description: 'UI preference for power users' },
        { id: 'feat-5', title: 'API rate limit increase', description: 'Unblocks integration partners' },
      ],
    });

    const riceSession = useToolSessionStore.getState().toolSessions.at(-1);
    if (!riceSession) return;
    const riceSid = riceSession.id;
    setRiceSessionId(riceSid);

    submitParticipantResponse({
      toolSessionId: riceSid,
      participantId: 'q1',
      participantName: 'Priya Nair',
      results: [
        { id: 'feat-1', name: 'Mobile push notifications', score: 85, rank: 1 },
        { id: 'feat-2', name: 'Bulk import via CSV', score: 60, rank: 3 },
        { id: 'feat-3', name: 'Role-based permissions', score: 70, rank: 2 },
        { id: 'feat-4', name: 'Dark mode', score: 30, rank: 5 },
        { id: 'feat-5', name: 'API rate limit increase', score: 55, rank: 4 },
      ],
    });

    submitParticipantResponse({
      toolSessionId: riceSid,
      participantId: 'q2',
      participantName: 'Tom Wells',
      results: [
        { id: 'feat-1', name: 'Mobile push notifications', score: 70, rank: 2 },
        { id: 'feat-2', name: 'Bulk import via CSV', score: 75, rank: 1 },
        { id: 'feat-3', name: 'Role-based permissions', score: 65, rank: 3 },
        { id: 'feat-4', name: 'Dark mode', score: 40, rank: 5 },
        { id: 'feat-5', name: 'API rate limit increase', score: 60, rank: 4 },
      ],
    });

    submitParticipantResponse({
      toolSessionId: riceSid,
      participantId: 'q3',
      participantName: 'Yuki Tanaka',
      results: [
        { id: 'feat-1', name: 'Mobile push notifications', score: 80, rank: 1 },
        { id: 'feat-2', name: 'Bulk import via CSV', score: 55, rank: 4 },
        { id: 'feat-3', name: 'Role-based permissions', score: 85, rank: 2, scoreBreakdown: { reach: 90, impact: 80, confidence: 85, effort: 70 } },
        { id: 'feat-4', name: 'Dark mode', score: 35, rank: 5 },
        { id: 'feat-5', name: 'API rate limit increase', score: 65, rank: 3 },
      ],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeSessionId = activeTab === 'classification' ? eisenhowerSessionId : riceSessionId;

  if (!activeSessionId) {
    return <div className="p-8 text-center text-[var(--text-ter)]">Loading demo…</div>;
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="bg-[var(--mustard-dim)] border-b border-[var(--mustard)] px-6 py-2 flex items-center gap-4">
        <span className="text-xs text-[var(--mustard)] font-medium flex-1">
          Demo mode — showing simulated participants with realistic disagreements
        </span>
        {/* Demo type tabs */}
        <div className="flex items-center gap-1 bg-[var(--mustard-dim)] p-0.5">
          <button
            onClick={() => setActiveTab('classification')}
            className={`px-3 py-1 text-xs font-medium  transition-colors ${
              activeTab === 'classification'
                ? 'bg-[var(--bg-raised)] text-[var(--mustard)] shadow-sm'
                : 'text-[var(--mustard)] hover:text-[var(--mustard)]'
            }`}
          >
            Classification (Eisenhower)
          </button>
          <button
            onClick={() => setActiveTab('ranking')}
            className={`px-3 py-1 text-xs font-medium  transition-colors ${
              activeTab === 'ranking'
                ? 'bg-[var(--bg-raised)] text-[var(--mustard)] shadow-sm'
                : 'text-[var(--mustard)] hover:text-[var(--mustard)]'
            }`}
          >
            Ranking (RICE)
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <AggregationView key={activeSessionId} toolSessionId={activeSessionId} />
      </div>
    </div>
  );
}
